/**
 * Labelled synthetic rows.
 *
 * Root CLAUDE.md: fixtures are labelled synthetic — id `OC-9xxxx`, alias prefixed `FIXTURE —` —
 * and never mixable with a real row. The `OC-9` prefix is the machine-readable half of that rule
 * and `isSyntheticId` is how every other package checks it.
 *
 * They are chosen to exercise the decisions the engine actually makes, not to look realistic:
 * a complete row, a thin one, one that cannot be priced at all, one whose profit basis is already
 * after an owner's wage, and one in a state with no labour rate of its own.
 */
import type { BusinessRow } from "./row.ts";

/** `OC-9xxxx`. A row failing this must never reach a client-facing surface. */
export function isSyntheticId(id: string): boolean {
  return /^OC-9[0-9]{4}$/.test(id);
}

const meta = (captured_by: string): BusinessRow["meta"] => ({
  captured_at: "2026-09-13T00:00:00Z",
  captured_by,
  schema_version: "1.3.0",
  stage: "indication",
  qa_status: "pending",
});

/** Everything answered: twelve of twelve, hours on two processes, priced sector, metro state. */
export const COMPLETE: BusinessRow = {
  id: "OC-90001",
  identity: {
    name_or_alias: "FIXTURE — strata cleaning, inner west", source: "bsale", source_url: null,
    state: "NSW", region: "metro", suburb: null, sector: "cleaning_facilities",
    sub_sector: "strata_cleaning", years_operating: 11, confidence: 2,
  },
  financials: {
    asking_price_aud: 1_200_000, revenue_aud: 1_450_000, revenue_trend: "flat",
    stated_profit_aud: 485_000, profit_basis: "sde", owner_wage_included: true,
    owner_hours_per_week: 22, days_to_invoice: 9, debtor_days: 47,
    recurring_revenue_pct: 55, top_customer_pct: 34, confidence: 2,
  },
  operations: {
    staff_fte: 9, staff_casual_count: 4, owner_role: "manages", second_in_command: false,
    key_processes: ["scheduling_dispatch", "invoicing", "collections", "customer_comms", "marketing"],
    hours_by_process: [
      { process: "scheduling_dispatch", hours_per_month_low: 30, hours_per_month_high: 45, basis: "stated" },
      { process: "collections", hours_per_month_low: 8, hours_per_month_high: 14, basis: "stated" },
    ],
    scheduling_method: "paper", invoicing_method: "manual", accounting_software: "xero",
    job_management_software: null, crm_present: false, website_quality_1_5: 3,
    online_booking_or_quote: false, reviews_count: 41, reviews_avg: 4.6, vehicles_or_sites: 3,
    notes: "Tried a scheduling app two years ago; the crews went back to the whiteboard.",
    confidence: 2,
  },
  sale: {
    reason_for_sale: "retirement", owner_age_band: "55-64", vendor_finance_offered: false,
    handover_weeks_offered: 12, lease_years_remaining: 4, confidence: 2,
  },
  meta: meta("fixture"),
};

/** Six of twelve and one hours figure: the thinnest row an indication may still be produced from. */
export const THIN: BusinessRow = {
  id: "OC-90002",
  identity: {
    name_or_alias: "FIXTURE — suburban electrical contractor", source: "seek_business",
    source_url: null, state: "VIC", region: "metro", sector: "trade_services",
    years_operating: 6, confidence: 1,
  },
  financials: { asking_price_aud: 450_000, stated_profit_aud: 180_000, profit_basis: "unknown", confidence: 1 },
  operations: {
    staff_fte: 4,
    key_processes: ["quoting", "scheduling_dispatch", "invoicing"],
    hours_by_process: [
      { process: "scheduling_dispatch", hours_per_month_low: 20, hours_per_month_high: 35, basis: "inferred" },
    ],
    scheduling_method: "spreadsheet", confidence: 1,
  },
  sale: { confidence: 1 },
  meta: meta("fixture"),
};

/** Five of twelve and no hours at all: `insufficient_input` (SCORING_SPEC §6), not a thin range. */
export const TOO_THIN: BusinessRow = {
  id: "OC-90003",
  identity: {
    name_or_alias: "FIXTURE — cafe, one line of listing text", source: "anybusiness",
    source_url: null, state: "QLD", region: "metro", sector: "hospitality", confidence: 1,
  },
  financials: { asking_price_aud: 320_000, confidence: 1 },
  operations: { staff_fte: 6, scheduling_method: "paper", confidence: 1 },
  sale: { confidence: 1 },
  meta: { ...meta("fixture"), free_text_listing: "Busy cafe, great location, owner retiring." },
};

/** Logistics used to be unpriceable under GA-70; R18 now proves the sourced-wage path. */
export const LOGISTICS: BusinessRow = {
  id: "OC-90004",
  identity: {
    name_or_alias: "FIXTURE — courier and last-mile logistics", source: "broker_direct",
    source_url: null, state: "WA", region: "metro", sector: "logistics",
    years_operating: 8, confidence: 2,
  },
  financials: {
    asking_price_aud: 900_000, revenue_aud: 2_100_000, stated_profit_aud: 340_000,
    profit_basis: "sde", owner_hours_per_week: 45, confidence: 2,
  },
  operations: {
    staff_fte: 14,
    key_processes: ["scheduling_dispatch", "invoicing", "customer_comms"],
    hours_by_process: [
      { process: "scheduling_dispatch", hours_per_month_low: 50, hours_per_month_high: 80, basis: "stated" },
      { process: "collections", hours_per_month_low: 10, hours_per_month_high: 18, basis: "stated" },
    ],
    scheduling_method: "spreadsheet", invoicing_method: "manual", accounting_software: "myob",
    online_booking_or_quote: false, notes: "Dispatch runs off a whiteboard and two phones.",
    confidence: 2,
  },
  sale: { reason_for_sale: "other_interests", confidence: 2 },
  meta: meta("fixture"),
};

/**
 * Tasmania, and a profit figure already after paying someone to do the owner's job. Exercises both
 * DQ-70's labour-rate fallback and the `owner_wage_included: false` branch of SCORING_SPEC §1.
 */
export const WAGE_ALREADY_TAKEN: BusinessRow = {
  id: "OC-90005",
  identity: {
    name_or_alias: "FIXTURE — allied health practice, Hobart", source: "owner_direct",
    source_url: null, state: "TAS", region: "metro", sector: "allied_health",
    years_operating: 14, confidence: 2,
  },
  financials: {
    asking_price_aud: 780_000, revenue_aud: 1_100_000, revenue_trend: "growing",
    stated_profit_aud: 260_000, profit_basis: "ebitda", owner_wage_included: false,
    owner_hours_per_week: 10, days_to_invoice: 1, debtor_days: 12,
    recurring_revenue_pct: 70, top_customer_pct: 8, confidence: 3,
  },
  operations: {
    staff_fte: 7, owner_role: "absentee", second_in_command: true,
    key_processes: ["scheduling_dispatch", "invoicing", "customer_comms", "practice_management"],
    hours_by_process: [
      { process: "practice_management", hours_per_month_low: 18, hours_per_month_high: 26, basis: "stated" },
      { process: "collections", hours_per_month_low: 4, hours_per_month_high: 7, basis: "inferred" },
    ],
    scheduling_method: "job_mgmt_software", invoicing_method: "accounting_software",
    accounting_software: "xero", job_management_software: "Cliniko", crm_present: true,
    website_quality_1_5: 4, online_booking_or_quote: true, reviews_count: 180, reviews_avg: 4.8,
    notes: "Practice manager runs the diary; the owner works one clinical day a fortnight.",
    confidence: 2,
  },
  sale: {
    reason_for_sale: "relocation", owner_age_band: "45-54", vendor_finance_offered: true,
    handover_weeks_offered: 8, lease_years_remaining: 6, confidence: 2,
  },
  meta: meta("fixture"),
};

/**
 * One person, no staff. `EVAL_BLIND_TEST`'s third named edge case.
 *
 * The interesting failure is not the arithmetic: it is that every hour saved is the owner's own,
 * so there is no one to delegate to and the bridge's owner multiplier is the whole story. A
 * business with `staff_fte: 0` must not divide by staff anywhere.
 */
export const ONE_PERSON: BusinessRow = {
  id: "OC-90006",
  identity: {
    name_or_alias: "FIXTURE — sole-trader electrician, Geelong", source: "owner_direct",
    source_url: null, state: "VIC", region: "regional", sector: "trade_services",
    years_operating: 6, confidence: 3,
  },
  financials: {
    asking_price_aud: 240_000, revenue_aud: 310_000, revenue_trend: "flat",
    stated_profit_aud: 145_000, profit_basis: "net_to_owner", owner_wage_included: true,
    owner_hours_per_week: 55, days_to_invoice: 9, debtor_days: 41,
    recurring_revenue_pct: 15, top_customer_pct: 22, confidence: 3,
  },
  operations: {
    staff_fte: 0, owner_role: "works_in", second_in_command: false,
    key_processes: ["quoting", "scheduling_dispatch", "invoicing", "collections"],
    hours_by_process: [
      { process: "quoting", hours_per_month_low: 14, hours_per_month_high: 22, basis: "stated" },
      { process: "invoicing", hours_per_month_low: 8, hours_per_month_high: 14, basis: "stated" },
      { process: "collections", hours_per_month_low: 5, hours_per_month_high: 9, basis: "inferred" },
    ],
    scheduling_method: "calendar_app", invoicing_method: "manual", accounting_software: "xero",
    crm_present: false, website_quality_1_5: 2, online_booking_or_quote: false,
    reviews_count: 28, reviews_avg: 4.9,
    notes: "Quotes written in the van after the last job; invoices go out on Sundays.",
    confidence: 3,
  },
  sale: {
    reason_for_sale: "health", owner_age_band: "55-64", handover_weeks_offered: 4, confidence: 3,
  },
  meta: meta("fixture"),
};

/**
 * Sixty per cent of revenue with one customer. `EVAL_BLIND_TEST`'s fourth named edge case.
 *
 * `SCORING_SPEC`'s concentration curve bottoms out at 70%, so 60% is deep in the penalty and the
 * risk belongs on page one. A report that prices this business without saying so is the failure.
 */
export const CONCENTRATED: BusinessRow = {
  id: "OC-90007",
  identity: {
    name_or_alias: "FIXTURE — contract packing, Western Sydney", source: "bsale",
    source_url: null, state: "NSW", region: "metro", sector: "manufacturing_light",
    years_operating: 17, confidence: 2,
  },
  financials: {
    asking_price_aud: 1_950_000, revenue_aud: 4_200_000, revenue_trend: "flat",
    stated_profit_aud: 610_000, profit_basis: "ebitda", owner_wage_included: true,
    owner_hours_per_week: 40, days_to_invoice: 3, debtor_days: 52,
    recurring_revenue_pct: 85, top_customer_pct: 60, rent_aud_pa: 190_000, confidence: 2,
  },
  operations: {
    staff_fte: 22, staff_casual_count: 9, owner_role: "manages", second_in_command: true,
    key_processes: ["scheduling_dispatch", "inventory", "invoicing", "compliance_reporting"],
    hours_by_process: [
      { process: "inventory", hours_per_month_low: 40, hours_per_month_high: 65, basis: "stated" },
      { process: "compliance_reporting", hours_per_month_low: 12, hours_per_month_high: 20, basis: "stated" },
      { process: "invoicing", hours_per_month_low: 10, hours_per_month_high: 16, basis: "inferred" },
    ],
    scheduling_method: "spreadsheet", invoicing_method: "accounting_software",
    accounting_software: "myob", crm_present: false, website_quality_1_5: 2,
    online_booking_or_quote: false, reviews_count: 6, reviews_avg: 3.9, vehicles_or_sites: 1,
    notes: "One customer has been 55-65% of volume for nine years; contract renews annually.",
    confidence: 2,
  },
  sale: {
    reason_for_sale: "retirement", owner_age_band: "65+", handover_weeks_offered: 26,
    lease_years_remaining: 2, confidence: 2,
  },
  meta: meta("fixture"),
};

/**
 * Professional services. `EVAL_BLIND_TEST`'s fifth named edge case, and the sector DQ-31 added
 * `practice_management` for — so it is also the fixture that proves A20 and A21 pool their hours
 * rather than double-counting them.
 */
export const PROFESSIONAL_SERVICES: BusinessRow = {
  id: "OC-90008",
  identity: {
    name_or_alias: "FIXTURE — suburban accounting practice", source: "broker_direct",
    source_url: null, state: "QLD", region: "metro", sector: "professional_services",
    sub_sector: "accounting", years_operating: 22, confidence: 3,
  },
  financials: {
    asking_price_aud: 1_400_000, revenue_aud: 1_850_000, revenue_trend: "growing",
    stated_profit_aud: 520_000, profit_basis: "sde", owner_wage_included: true,
    owner_hours_per_week: 48, days_to_invoice: 14, debtor_days: 63,
    recurring_revenue_pct: 78, top_customer_pct: 11, rent_aud_pa: 64_000, confidence: 3,
  },
  operations: {
    staff_fte: 11, owner_role: "works_in", second_in_command: false,
    key_processes: ["practice_management", "invoicing", "collections", "customer_comms"],
    hours_by_process: [
      { process: "practice_management", hours_per_month_low: 45, hours_per_month_high: 70, basis: "stated" },
      { process: "collections", hours_per_month_low: 16, hours_per_month_high: 28, basis: "stated" },
      { process: "customer_comms", hours_per_month_low: 20, hours_per_month_high: 30, basis: "inferred" },
    ],
    scheduling_method: "calendar_app", invoicing_method: "accounting_software",
    accounting_software: "xero", crm_present: false, website_quality_1_5: 3,
    online_booking_or_quote: false, reviews_count: 34, reviews_avg: 4.5,
    notes: "Workflow tracked on a whiteboard and a shared spreadsheet; two partners chase debtors.",
    confidence: 3,
  },
  sale: {
    reason_for_sale: "retirement", owner_age_band: "65+", vendor_finance_offered: true,
    handover_weeks_offered: 52, lease_years_remaining: 3, confidence: 3,
  },
  meta: meta("fixture"),
};

/**
 * A complete, human-style owner interview for exercising intake → report workflows.
 * Every answer and financial figure below is invented for testing. It must never be described as
 * an actual Australian business, used as release evidence, or inserted into staging/production.
 */
export const SYNTHETIC_OWNER_INTERVIEW = {
  test_only: true,
  label: "SYNTHETIC TEST OWNER — NOT A REAL PERSON OR BUSINESS",
  answers: {
    1: "We do commercial and end-of-lease cleaning around Adelaide, South Australia.",
    2: "There are six of us including me: four full-time staff and one casual cleaner, plus me.",
    3: "We've been operating for nine years.",
    4: "Most work comes from property managers and referrals. A few people find us through the website.",
    5: "I answer the phone between jobs. If I miss a call, I usually call back after work. There is no online quote form.",
    6: "I put jobs into a shared Google calendar. Changes still get texted around to the team.",
    7: "Scheduling takes me about five hours a week, including changes and letting the cleaners know where to go.",
    8: "I make invoices in Xero, usually within two working days of the job.",
    9: "I spend roughly four hours a month following up overdue invoices.",
    10: "We use Xero for accounts and Google Calendar for jobs. We don't have a separate job-management system.",
    11: "I spend about twelve hours a week on admin and the rest is split between quoting, site checks and helping the team.",
    12: "We tried a cleaning-job app about three years ago. The staff found it fiddly, so we stopped using it and went back to the calendar and text messages.",
  },
} as const;

/** Structured row mapped from the synthetic interview above; all numbers are invented test inputs. */
export const SYNTHETIC_OWNER_DEMO: BusinessRow = {
  id: "OC-90009",
  identity: {
    name_or_alias: "FIXTURE — owner interview, Adelaide commercial cleaning",
    source: "owner_direct", source_url: null, state: "SA", region: "metro",
    sector: "cleaning_facilities", sub_sector: "commercial_cleaning", years_operating: 9,
    confidence: 2,
  },
  financials: {
    asking_price_aud: 690_000, revenue_aud: 920_000, revenue_trend: "growing",
    stated_profit_aud: 180_000, profit_basis: "sde", owner_wage_included: true,
    owner_hours_per_week: 12, days_to_invoice: 2, debtor_days: 31,
    recurring_revenue_pct: 62, top_customer_pct: 24, confidence: 2,
  },
  operations: {
    staff_fte: 4.5, staff_casual_count: 1, owner_role: "manages", second_in_command: false,
    key_processes: ["lead_capture", "quoting", "scheduling_dispatch", "service_delivery", "invoicing", "collections", "customer_comms"],
    // Five hours each week ≈ 22–24 hours in a four-and-a-third-week month.
    hours_by_process: [
      { process: "scheduling_dispatch", hours_per_month_low: 20, hours_per_month_high: 24, basis: "stated" },
      { process: "collections", hours_per_month_low: 4, hours_per_month_high: 4, basis: "stated" },
    ],
    scheduling_method: "calendar_app", invoicing_method: "accounting_software",
    accounting_software: "xero", job_management_software: null, crm_present: false,
    website_quality_1_5: 3, online_booking_or_quote: false, reviews_count: 38, reviews_avg: 4.7,
    vehicles_or_sites: 2,
    notes: "SYNTHETIC Q5: owner answers between jobs and calls missed enquiries back after work; no online quote form. SYNTHETIC Q12: a cleaning-job app was abandoned after staff found it fiddly; work returned to a shared calendar and text messages.",
    confidence: 2,
  },
  sale: { confidence: 1 },
  meta: meta("fixture"),
};

export const FIXTURES: Record<string, BusinessRow> = {
  COMPLETE, THIN, TOO_THIN, LOGISTICS, WAGE_ALREADY_TAKEN,
  ONE_PERSON, CONCENTRATED, PROFESSIONAL_SERVICES, SYNTHETIC_OWNER_DEMO,
};
